import React from 'react';
import { Elements } from '@stripe/react-stripe-js';
import useCartCost from '../hooks/useCartCost';
import useCheckoutState from '../hooks/useCheckoutState';
import getStripeAccount from '../lib/getStripeAccount';
import getUpsellFunnelStep from '../lib/getUpsellFunnelStep';
import { getMinimumChargeAmount } from '../services/currency';
import { useMemo } from 'react';
import { PAGE_TYPES } from '../enums/page-types';
import getStripe from '../lib/get-stripe';

const paymentElementAppearance = {
  variables: {
    colorPrimary: '#382b2f',
    borderRadius: '4px',
    colorBackground: '#ffffff',
    colorTextPlaceholder: '#c0b6ba',
    colorIcon: '#c0b6ba',
    fontWeightNormal: 400,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
    fontSmooth: 'always',
    fontSizeBase: '16px',
  },
  rules: {
    '.Label': {
      color: '#000000',
      fontWeight: 500,
      marginBottom: '4px',
      lineHeight: 1.5,
    },
    '.Input': {
      lineHeight: 1.5,
      borderStyle: 'none',
      borderColor: '#c0b6ba',
      paddingTop: '8px',
      paddingBottom: '8px',
      boxShadow: 'inset 0 0 0 1px #e0d9dd, 0 1px 1.5px 0 rgba(0, 0, 0, 0.05)',
    },
    '.Input:focus': {
      outline: '2px solid transparent',
      outlineOffset: '2px',
      boxShadow:
        'rgb(255, 255, 255) 0px 0px 0px 0px, rgb(192, 182, 186) 0px 0px 0px 1px, rgb(192, 182, 186) 0px 0px 0px 1px inset, rgba(0, 0, 0, 0.05) 0px 1px 1.5px 0px',
    },
    '.Tab': {
      borderColor: '#e0d9dd',
    },
    '.Tab:focus': {
      boxShadow: 'none',
    },
    '.Tab--selected:focus': {
      boxShadow: '0 0 0 1px var(--colorPrimary)',
    },
  },
};

function getStripeElementOptions({
  pageType,
  totalDueAmount,
  currency,
  mode,
  appearance,
  shouldSavePaymentMethod,
}) {
  if (pageType === PAGE_TYPES.FORM) {
    return {
      appearance,
    };
  }

  // if totalDueAmount is not a number, use minimum charge amount
  // multiply by 2 to ensure that the total amount is at least the minimum charge amount
  // https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
  // allow 0 for subscriptions (free trial or coupons)
  const minimumChargeAmount = getMinimumChargeAmount(currency);
  const amount =
    totalDueAmount === 0 && mode === 'subscription'
      ? 0
      : Number(totalDueAmount) > minimumChargeAmount
        ? Number(totalDueAmount)
        : minimumChargeAmount * 2;

  return {
    appearance,
    amount: amount,
    currency: currency,
    mode: mode,
    ...(shouldSavePaymentMethod && { setupFutureUsage: 'off_session' }),
    paymentMethodOptions: {
      us_bank_account: {
        verification_method: 'instant',
      },
    },
  };
}

const StripeElementsProvider = ({ children }) => {
  const { isTesting, seller, checkout, publicKey, publicKeyTest } = useCheckoutState();

  const { totalDueAmount, currency, mode } = useCartCost();

  const stripeAccount = useMemo(() => getStripeAccount(seller), [seller]);

  const shouldSavePaymentMethod = useMemo(() => {
    if (checkout.savePaymentMethod) {
      return true;
    }

    const hasUpsellFunnelStep = getUpsellFunnelStep(checkout);

    if (hasUpsellFunnelStep) {
      return true;
    }

    return false;
  }, [checkout]);

  const stripeElementOptions = useMemo(
    () =>
      getStripeElementOptions({
        pageType: checkout.type,
        totalDueAmount,
        currency,
        mode,
        appearance: paymentElementAppearance,
        shouldSavePaymentMethod,
      }),
    [checkout, totalDueAmount, currency, mode, shouldSavePaymentMethod],
  );

  return (
    <Elements
      stripe={getStripe({ stripeAccount, isTesting, publicKey, publicKeyTest })}
      options={stripeElementOptions}
    >
      {children}
    </Elements>
  );
};

export default StripeElementsProvider;
